import { makeObservable, observable, computed, action } from "mobx";
import { ContentSource } from "../../lib/ContentSource";
import { DbRecordedEventSearchState } from "./DbRecordedEventSearch";
import { InferrerOfSearchTermsFromDomEvents } from "./InferrerOfSearchTermsFromDomEvents";
import { AllOption, SingleOriginFilterState } from "./SingleOriginFilterState";
import type { RecordingViewState } from "./RecordingViewState";
import { isNetworkEvent, NetworkEvent } from "../../lib/Events";
import { getRegisteredDomain } from "../../lib/utils/domains";
import { describeEncodedSearchTermUsingFunctionNotationHtml } from "../../lib/Search/EncodedSearchTerm";
import { mapGetOrCreate } from "../../lib/utils/mapGetOrCreate";
import { DbRecordedEvent } from "../../State/EvidenceDB/DatabaseTypes";
import { ExtensionSettings } from "../../State/EvidenceDB/DbExtensionSettings";
import { ContentSourceSearchResult } from "../../lib/searchRecordedEvent";


export class RecordingSearchState {
	_searchTermTextAreaValue: string | undefined;
	_showOnlySearchTerm: string | undefined;
	_dataFlowDirection: AllOption | ContentSource["direction"] = 'sent';
	readonly destinationUrlFilterState: SingleOriginFilterState;

	get dbRecordingEntryWriter() { return this.recordingViewState.dbRecordingEntryWriter; }
	get networkTrafficRecordingState() { return this.recordingViewState.networkTrafficRecordingState; }
	get sourcePageUrlFilterState() { return this.recordingViewState.sourcePageUrlFilterState; }

	constructor(
		public readonly recordingViewState: RecordingViewState,
	) {
		this.destinationUrlFilterState = new SingleOriginFilterState(
			this.recordingViewState.getDestinationNetworkStatisticsTree, () => this.sourcePageUrlFilterState.domainOrUrlToFilterFor
		);
		makeObservable(this, {
			//			_tabIdRecordedToUrlsRecorded: observable,
			_searchTermTextAreaValue: observable,
			_showOnlySearchTerm: observable,
			_dataFlowDirection: observable,
		});
	}

	@computed get recording() { return this.dbRecordingEntryWriter.recording; }
	@computed get recordingUuid() { return this.recording.recordingUuid; }

	@computed get searchTermTextAreaValue() {
		return this._searchTermTextAreaValue ??
			this.dbRecordingEntryWriter.recording.searchTermsNewlineSeparated ?? "";
	}
	setSearchTermTextAreaValue = action((value: string) => {
		this._searchTermTextAreaValue = value;
		this.dbRecordingEntryWriter.updateRecording({
			searchTermsNewlineSeparated: value
		});
	});

	@computed get GlobalSearchTermTextAreaValue() {
		if (window.location.protocol.startsWith("file")) {
			console.log("GlobalSearchTermTextAreaValue", this.recording, this.recording.globalSearchTermsNewlineSeparated);
			return this.recording.globalSearchTermsNewlineSeparated ?? "";
		} else {
			console.log("GlobalSearchTermTextAreaValue for extension");
			return ExtensionSettings.current?.sharedSearchTermField ?? "";
		}
	}

	@computed get GlobalSearchTermArray() {
		return this.GlobalSearchTermTextAreaValue.split("\n").map( s => s.trim() ).filter( s => s.length > 0);
	}


	setGlobalSearchTermAreaValue = async (value: string) => {
		await Promise.all([
			ExtensionSettings.current?.updateSetting("sharedSearchTerms", value),
			this.dbRecordingEntryWriter.updateRecording({
				globalSearchTermsNewlineSeparated: value
			}),	
		])
	};

	@computed get searchTermsFromTextArea(): string[] {
		if (this._searchTermTextAreaValue == null) {
			return (this.recording.searchTermsNewlineSeparated ?? "").split("\n")
			.map(s => s.trim())
			.filter(s => s.length > 0).sort();
		} else {
			return this._searchTermTextAreaValue.split("\n")
				.map(s => s.trim())
				.filter((line) => line.length >= 3);
		}
	}

	@computed get searchTerms(): string[] {
		const result = [...new Set<string>([
			...this.searchTermsFromDomEvents,
			...this.searchTermsFromTextArea,
			...this.GlobalSearchTermArray,
		]
		).values()]
			.filter(s => s.length >= 3)
			.sort();
		// ProcessInfo.log("searchTerms", toJS(result));
		return result;
	}

	@computed protected get inferrerOfSearchTermsFromDomEvents() {
		const { dbRecordingEntryWriter } = this;
		if (dbRecordingEntryWriter == null) return;
		return new InferrerOfSearchTermsFromDomEvents(dbRecordingEntryWriter);
	}

	@computed get searchTermsFromDomEvents(): string[] {
		return this.inferrerOfSearchTermsFromDomEvents?.searchTermsFromDomEventsSortedAlphanumeric ?? [];
	}

	@computed get showOnlySearchTerm() {
		return (
			this._showOnlySearchTerm == null ||
			!this.searchTerms.includes(this._showOnlySearchTerm)
		) ?
			undefined :
			this._showOnlySearchTerm;
	}
	setShowOnlySearchTerm = action((searchTerm: string | undefined) => this._showOnlySearchTerm = searchTerm);
	get dataFlowDirection() { return this._dataFlowDirection; }
	setDataFlowDirection = action((direction: string) => {
		this._dataFlowDirection = direction === "sent" || direction === "received" ? direction : AllOption;
	});

	@computed get searchState(): DbRecordedEventSearchState {
		return DbRecordedEventSearchState.forRecordingUuidAndSearchTerms(this.recording.recordingUuid, this.searchTerms, this.recordingViewState.windowRoutingState.injectedContext.searchWorkerUrl);
	}

	@computed protected get searchResultsForReports() {
		const byDestinationDomainMap = 
			new Map<string /* destination registered domain */,
				Map<string /* source registered domain */,
					Map<string /* original search term */,
						Map<string /* encoding string */,
							{event: DbRecordedEvent<NetworkEvent>, searchResult: ContentSourceSearchResult}[]>>>>();

		const byDomain =
			new Map<string /* source registered domain */,
				Map<string /* destination registered domain */,
					Map<string /* original search term */,
						Map<string /* encoding string */,
							{event: DbRecordedEvent<NetworkEvent>, searchResult: ContentSourceSearchResult}[]>>>>();

		const bySearchTermMap =
			new Map<string /* original search term */,
				Map<string /* destination registered domain */,
					Map<string /* source registered domain */,
						Map<string /* encoding string */,
						{event: DbRecordedEvent<NetworkEvent>, searchResult: ContentSourceSearchResult}[]>>>>();
		for (const {searchResults, ...event} of this.searchState.results.filter( e => isNetworkEvent(e))) {			
			const {tab, destination} = event;
			const sourceRegisteredDomain = getRegisteredDomain(tab.url);
			const destinationRegisteredDomain = getRegisteredDomain(destination.url);
			if (sourceRegisteredDomain == null || destinationRegisteredDomain == null) continue;
			for (const searchResult of searchResults) {
				const {originalSearchTerm, encodingSteps, direction} = searchResult;
				if (direction !== 'sent') continue;
				const encodingString = describeEncodedSearchTermUsingFunctionNotationHtml(encodingSteps, originalSearchTerm);
				
				const byDestinationDomain = mapGetOrCreate(byDestinationDomainMap, destinationRegisteredDomain, () => new Map());
				const byDestinationDomainSd = mapGetOrCreate(byDestinationDomain, sourceRegisteredDomain, () => new Map());
				const byDestinationDomainSdSt = mapGetOrCreate(byDestinationDomainSd, originalSearchTerm, () => new Map());
				const byDestinationDomainSdStEncoding = mapGetOrCreate(byDestinationDomainSdSt, encodingString, () => []);
				byDestinationDomainSdStEncoding.push({searchResult, event});

				const byDomainSd = mapGetOrCreate(byDomain, sourceRegisteredDomain, () => new Map());
				const byDomainSdDd = mapGetOrCreate(byDomainSd, destinationRegisteredDomain, () => new Map());
				const byDomainSdDdSt = mapGetOrCreate(byDomainSdDd, originalSearchTerm, () => new Map());
				const byDomainSdDdStEncoding = mapGetOrCreate(byDomainSdDdSt, encodingString, () => []);
				byDomainSdDdStEncoding.push({searchResult, event});

				const bySearchTermSt = mapGetOrCreate(bySearchTermMap, originalSearchTerm, () => new Map());
				const bySearchTermStDd = mapGetOrCreate(bySearchTermSt, destinationRegisteredDomain, () => new Map());
				const bySearchTermStDdSd = mapGetOrCreate(bySearchTermStDd, sourceRegisteredDomain, () => new Map());
				const bySearchTermStDdSdEncoding = mapGetOrCreate(bySearchTermStDdSd, encodingString, () => []);
				bySearchTermStDdSdEncoding.push({searchResult, event});
			}
		}

		const byDestinationDomain = [...byDestinationDomainMap.entries()].map( ([destinationRegisteredDomain, bySource]) => {
			const sources = [...bySource.entries()].map( ([sourceRegisteredDomain, bySearchTermMap]) => {
				const searchTerms = [...bySearchTermMap.entries()].map( ([originalSearchTerm, byEncodingMap]) => {
					const encodings = [...byEncodingMap.entries()].map( ([encodingString, eventsAndResults]) => {
						const inferredTypeNames = [...new Set<string>(eventsAndResults.map( ({searchResult}) => searchResult.inferredDataTypeName).filter( s => s != null))].toSorted();						
						return {encodingString, eventsAndResults, inferredTypeNames};
					});
					const inferredTypeNames = [...new Set<string>(([] as string[]).concat(...encodings.map( e => e.inferredTypeNames)))].toSorted();
					return {originalSearchTerm, encodings, inferredTypeNames};
				}).sort( (a, b) => a.originalSearchTerm.localeCompare(b.originalSearchTerm));
				const inferredTypeNames = [...new Set<string>(([] as string[]).concat(...searchTerms.map( e => e.inferredTypeNames)))].toSorted();
				return {sourceRegisteredDomain, searchTerms, inferredTypeNames};
			}).sort( (a, b) => a.sourceRegisteredDomain.localeCompare(b.sourceRegisteredDomain));
			const inferredTypeNames = [...new Set<string>(([] as string[]).concat(...sources.map( e => e.inferredTypeNames)))].toSorted();
			return {destinationRegisteredDomain, sources, inferredTypeNames};
		}).sort( (a, b) => a.destinationRegisteredDomain.localeCompare(b.destinationRegisteredDomain));


		const bySourceRegisteredDomain = [...byDomain.entries()].map( ([sourceRegisteredDomain, byDestination]) => {
			const destinations = [...byDestination.entries()].map( ([destinationRegisteredDomain, bySearchTermMap]) => {
				const searchTerms = [...bySearchTermMap.entries()].map( ([originalSearchTerm, byEncodingMap]) => {
					const encodings = [...byEncodingMap.entries()].map( ([encodingString, eventsAndResults]) => {
						const inferredTypeNames = [...new Set<string>(eventsAndResults.map( ({searchResult}) => searchResult.inferredDataTypeName).filter( s => s != null))].toSorted();
						return {encodingString, eventsAndResults, inferredTypeNames};
					});
					const inferredTypeNames = [...new Set<string>(([] as string[]).concat(...encodings.map( e => e.inferredTypeNames)))].toSorted();
					return {originalSearchTerm, encodings, inferredTypeNames};
				}).sort( (a, b) => a.originalSearchTerm.localeCompare(b.originalSearchTerm));
				const inferredTypeNames = [...new Set<string>(([] as string[]).concat(...searchTerms.map( e => e.inferredTypeNames)))].toSorted();
				return {destinationRegisteredDomain, searchTerms, inferredTypeNames};
			}).sort( (a, b) => a.destinationRegisteredDomain.localeCompare(b.destinationRegisteredDomain));
			const inferredTypeNames = [...new Set<string>(([] as string[]).concat(...destinations.map( e => e.inferredTypeNames)))].toSorted();
			return {sourceRegisteredDomain, destinations};
		}).sort( (a, b) => a.sourceRegisteredDomain.localeCompare(b.sourceRegisteredDomain));

		const bySearchTerm = [...bySearchTermMap.entries()].map( ([searchTerm, byDestination]) => {
			const destinations = [...byDestination.entries()].map( ([destinationRegisteredDomain, bySourcePageMap]) => {
				const sourcePages = [...bySourcePageMap.entries()].map( ([sourceRegisteredDomain, byEncodingMap]) => {
					const encodings = [...byEncodingMap.entries()].map( ([encodingString, eventsAndResults]) => {
						const inferredTypeNames = [...new Set<string>(eventsAndResults.map( ({searchResult}) => searchResult.inferredDataTypeName).filter( s => s != null))].toSorted();
						return {encodingString, eventsAndResults, inferredTypeNames};
					});
					const inferredTypeNames = [...new Set<string>(([] as string[]).concat(...encodings.map( e => e.inferredTypeNames)))].toSorted();
					return {sourceRegisteredDomain, encodings, inferredTypeNames};
				}).sort( (a, b) => a.sourceRegisteredDomain.localeCompare(b.sourceRegisteredDomain));
				const inferredTypeNames = [...new Set<string>(([] as string[]).concat(...sourcePages.map( e => e.inferredTypeNames)))].toSorted();
				return {destinationRegisteredDomain, sourcePages, inferredTypeNames};
			}).sort( (a, b) => a.destinationRegisteredDomain.localeCompare(b.destinationRegisteredDomain));
			const inferredTypeNames = [...new Set<string>(([] as string[]).concat(...destinations.map( e => e.inferredTypeNames)))].toSorted();
			return {searchTerm, destinations, inferredTypeNames};
		}).sort( (a, b) => a.searchTerm.localeCompare(b.searchTerm));
		return {byDestinationDomain, bySourceRegisteredDomain, bySearchTerm};
	}

	@computed get searchResultsBySourceRegisteredDomain() {
		return this.searchResultsForReports.bySourceRegisteredDomain;
	}
	@computed get searchResultsBySearchTerm() {
		return this.searchResultsForReports.bySearchTerm;
	}
	@computed get searchResultsByDestinationDomain() {
		return this.searchResultsForReports.byDestinationDomain;
	}

}

